import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import './Admin.css';
import CustomerInfoModal from '../../components/support/CustomerInfoModal';
import TranslatorInfoModal from '../../components/support/TranslatorInfoModal';
const API_URL = process.env.REACT_APP_API_URL;
const FinancialSummary = ({ data, t, formatCurrency }) => (
  <div className="stats-summary-card">
    <div className="summary-item"><h3>{t('total_revenue')}</h3><p>{formatCurrency(data.total_revenue)}</p></div>
    <div className="summary-item"><h3>{t('total_translator_payments')}</h3><p>{formatCurrency(data.total_cost)}</p></div>
    <div className="summary-item profit"><h3>{t('net_profit')}</h3><p>{formatCurrency(data.total_profit)}</p></div>
  </div>
);

const TranslatorStatsTable = ({ data, t, formatCurrency, searchTerm, onViewDetails }) => {
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return data.filter(user => 
            `${user.first_name} ${user.last_name}`.toLowerCase().includes(lowerCaseSearch) ||
            user.email.toLowerCase().includes(lowerCaseSearch)
        );
    }, [data, searchTerm]);

    return (
        <div className="admin-table-container">
            <h2>{t('translator_stats')}</h2>
            <table className="admin-table">
                <thead><tr><th>{t('translator_check')}</th><th>{t('active_jobs_table')}</th><th>{t('completed_jobs_count')}</th><th>{t('total_earnings')}</th></tr></thead>
                <tbody>
                {filteredData.map(tStat => (
                    <tr key={`t-${tStat.id}`}>
                        <td>
                            <div className="user-cell">
                                <Link to={`/management/translators/${tStat.id}`} className="link-button main-link">
                                    {tStat.first_name} {tStat.last_name}
                                </Link>
                                {/* DÜZELTME: onUserClick -> onViewDetails olarak değiştirildi */}
                                <button onClick={() => onViewDetails(tStat)} className="info-icon-btn" title={t('view_contact_info')}>
                                    <i className="fas fa-info-circle"></i>
                                </button>
                            </div>
                        </td>
                        <td>{tStat.active_jobs}</td>
                        <td>{tStat.completed_jobs}</td>
                        <td>{formatCurrency(tStat.total_earnings)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const CustomerStatsTable = ({ data, t, formatCurrency, onViewDetails, searchTerm }) => {
    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return data.filter(user => 
            `${user.first_name} ${user.last_name}`.toLowerCase().includes(lowerCaseSearch) ||
            user.email.toLowerCase().includes(lowerCaseSearch)
        );
    }, [data, searchTerm]);

    return (
        <div className="admin-table-container">
            <h2>{t('customer_stats')}</h2>
            <table className="admin-table">
                <thead><tr><th>{t('customer_table')}</th><th>{t('total_orders_count_table')}</th><th>{t('total_spent_amount')}</th></tr></thead>
                <tbody>
                {filteredData.map(cStat => (
                    <tr key={`c-${cStat.id}`}>
                        <td><button className="link-button" onClick={() => onViewDetails(cStat)}>{cStat.first_name} {cStat.last_name}</button></td>
                        <td>{cStat.total_orders}</td>
                        <td>{formatCurrency(cStat.total_spent)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

const AdminStatsPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
   const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'summary');
  const [timeRange, setTimeRange] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isTranslatorModalOpen, setIsTranslatorModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchStats = useCallback(async (range) => {
    if (!token) return;
    try {
      setLoading(true); setError('');
      const response = await fetch(`${API_URL}/api/admin/stats?range=${range}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) {
          const data = await response.json();
          throw new Error(t(data.messageKey) || t('error_fetch_stats'));
      }
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchStats(timeRange);
  }, [fetchStats, timeRange]);

  const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString(i18n.language, {style:'currency', currency:'TRY'});

  const handleViewUserDetails = (user) => {
      setSelectedUser(user);
      if (user.role === 'customer') {
          setIsCustomerModalOpen(true);
      } else if (user.role === 'translator') {
          const translatorData = { ...user, completed_jobs: user.completed_jobs, total_orders: undefined };
          setSelectedUser(translatorData);
          setIsTranslatorModalOpen(true);
      }
  };

  const renderContent = () => {
    if (loading) return <div className="loading-indicator"><i className="fas fa-spinner fa-spin"></i></div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!stats) return <p>{t('no_stats_found')}</p>;

    switch(activeTab) {
        case 'summary':
            return <FinancialSummary data={stats.financials} t={t} formatCurrency={formatCurrency} />;
        case 'translators':
            return <TranslatorStatsTable data={stats.translatorStats} t={t} formatCurrency={formatCurrency} onViewDetails={handleViewUserDetails} searchTerm={searchTerm} />;
        case 'customers':
            return <CustomerStatsTable data={stats.customerStats} t={t} formatCurrency={formatCurrency} onViewDetails={handleViewUserDetails} searchTerm={searchTerm} />;
        default:
            return null;
    }
  };
  
  return (
    <>
      <div className="page-container admin-dashboard">
        <div className="page-header">
            <h1 className="stats-page-title">{t('statistics')}</h1>
          <div className="header-filters">
                {activeTab === 'summary' && (
                <div className="time-range-filter modern">
                    <button onClick={() => setTimeRange('day')} className={timeRange === 'day' ? 'active' : ''}>{t('daily')}</button>
                    <button onClick={() => setTimeRange('month')} className={timeRange === 'month' ? 'active' : ''}>{t('monthly')}</button>
                    <button onClick={() => setTimeRange('year')} className={timeRange === 'year' ? 'active' : ''}>{t('yearly')}</button>
                    <button onClick={() => setTimeRange('all')} className={timeRange === 'all' ? 'active' : ''}>{t('all_time')}</button>
                </div>
            )}
            {(activeTab === 'translators' || activeTab === 'customers') && (
                <div className="stats-search-bar">
                    <i className="fas fa-search"></i>
                    <input type="text" placeholder={activeTab === 'translators' ? t('search_translator_placeholder') : t('search_customer_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
            )}
          </div>
        </div>
        
        <div className="stats-container">
          <nav className="stats-tabs">
              <button onClick={() => { setActiveTab('summary'); setSearchTerm(''); }} className={activeTab === 'summary' ? 'active' : ''}>{t('summary')}</button>
              <button onClick={() => { setActiveTab('translators'); setSearchTerm(''); }} className={activeTab === 'translators' ? 'active' : ''}>{t('translators')}</button>
              <button onClick={() => { setActiveTab('customers'); setSearchTerm(''); }} className={activeTab === 'customers' ? 'active' : ''}>{t('customers')}</button>
          </nav>
          <div className="stats-content">
              {renderContent()}
          </div>
        </div>
      </div>
      <CustomerInfoModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} customer={selectedUser} />
      <TranslatorInfoModal isOpen={isTranslatorModalOpen} onClose={() => setIsTranslatorModalOpen(false)} translator={selectedUser} />
    </>
  );
};

export default AdminStatsPage;